import { useContext, useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { Listeners } from './ui'
import { IListeners } from './types'

import { useListeners } from '@api/listeners/state'

import { useValue } from '@hooks/useValue'
import { useDelayedInput } from '@hooks/useDelayedInput'

import { CardModeType } from '@smart/common.type'
import { CardListenerContainer, ICardListenerContainer } from '@smart/cardListener'
import { IListenerJourneyContainer, ListenerJourneyContainer } from '@smart/listenerJourney'
import { IModalPreview, ModalPreview } from '@primitive/modal.preview'

import { NotificationContext, notifyOpen } from "@ui/notification/notification.provider"


/** Слушатели - Контейнер */
export const ListenersContainer = () => {
    const notify = useContext(NotificationContext)

    const isAccessDeniedMessage = (message?: string) => {
        const normalizedMessage = (message || '').toLowerCase()

        return normalizedMessage.includes('доступ') || normalizedMessage.includes('допуск')
    }

    /** Метка о том, что доступ к данному разделу запрещен */
    const isAccessDenied = useValue<boolean>(false)

    const listenersState = useListeners(useShallow((state) => state))
    const cardListener = useValue<ICardListenerContainer>({isOpen: false})
    const listenerJourney = useValue<IListenerJourneyContainer>({isOpen: false})
    const modalPreview = useValue<IModalPreview>({isOpen: false})

    const currentPageNumber = useValue<number>(1)


    useEffect(() => {
        return (() => {
            listenersState.clearListenersState()

            currentPageNumber.setValue(1)
            setTxtSearch('')
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect(() => {
        if (!listenersState.error) return
        if (isAccessDenied.value || isAccessDeniedMessage(listenersState.error.message)) return

        return notify && notifyOpen(listenersState.error.message, listenersState.error.type, 3000, notify)
    }, [listenersState.error, isAccessDenied.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadPrimaryData(txtSearch)
    }, [currentPageNumber.value]) // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        listenersState.isReloadListeners && loadPrimaryData(txtSearch)
    }, [listenersState.isReloadListeners]) // eslint-disable-line react-hooks/exhaustive-deps

    // =======================================================================

    /** Функция по получению списка слушателей */
    const loadPrimaryData = (search?: string) => {
        listenersState.actionGetListeners(currentPageNumber.value, search || '').then((res) => {
            if (res.status !== 200 && res.status !== 500) {
                if (res.status === 403) {
                    listenersState.clearListenersState()
                    isAccessDenied.setValue(true)
                } else {
                    notify && notifyOpen(res.message, 'error', 2000, notify)
                }
            }
        })
    }

    /** Запуск загрузки данных через полсекунды */
    const { value: txtSearch, setValue: setTxtSearch } = useDelayedInput<string>("", 500, (val) => loadPrimaryData(val))

    // =======================================================================

    /** Открытие/закрытие карточки по слушателю */
    const handleOpenCardListener = (isOpen: boolean, cardMode: CardModeType, cardID?: number) => {
        cardListener.setValue({
            isOpen, cardMode, cardID,
            handlerButtonCancel() {cardListener.setValue({isOpen: false})}, 
            handlerButtonOk() {
                cardListener.setValue({isOpen: false})
                loadPrimaryData(txtSearch)
            }
        })
    }

    const handleOpenListenerJourney = (listenerID: number) => {
        listenerJourney.setValue({
            isOpen: true,
            listenerID,
            onClose: () => listenerJourney.setValue({isOpen: false}),
        })
    }

    /** Открытие/закрытие окна предпросмотра документа */
    const handleOpenModalPreview = (isOpen: boolean, fileUrl: string, fileName: string) => {
        modalPreview.setValue({
            isOpen, fileUrl, fileName, 
            handleButtonCancel() {modalPreview.setValue({isOpen: false})}, 
        })
    }

    /** Функция по скачиванию файлов контрагента */
    const handleDownloadFiles = (filePath: string, fileName: string) => {
        const link = document.createElement('a') // Создание ссылки и программное скачивание файла

        link.href = filePath
        link.download = fileName
        document.body.appendChild(link)

        link.click()
        document.body.removeChild(link)
    }

    //=======================================================================

    /** Свойства передаваемые в компоненту */
    const propsToComponent: IListeners = {
        isLoading: listenersState.listeners.isLoading,
        isAccessDenied,
        txtSearch, setTxtSearch, loadPrimaryData,

        currentPageNumber, 
        totalCount: listenersState.listeners.totalCount, 
        pageSize: listenersState.listeners.pageSize,

        listenersList: listenersState.listeners.items,

        handleOpenCardListener, handleOpenListenerJourney, handleOpenModalPreview, handleDownloadFiles
    }
    
    return <>
        <Listeners {...propsToComponent} />
        <CardListenerContainer {...cardListener.value} />
        <ListenerJourneyContainer {...listenerJourney.value} />

        <ModalPreview {...modalPreview.value} />
    </> 
}